import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useParticipant } from '@/contexts/ParticipantContext';
import { MarketTicker } from '@/components/dashboard/MarketTicker';
import {
  LayoutDashboard, TrendingUp, List, ClipboardList,
  ShieldCheck, Lock, Menu, LogOut, Settings,
  Banknote, Users, ArrowRightLeft, Calculator, Newspaper, User, FolderOpen,
  Download, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';

const ADMIN_ROLES = new Set(['admin', 'compliance', 'operations', 'restricted_admin']);

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Investments', path: '/investments', icon: TrendingUp },
  { label: 'Fund Menu', path: '/funds', icon: List },
  { label: 'Interfund Transfer', path: '/ift', icon: ArrowRightLeft, badge: 'iftRemaining' },
  { label: 'Order History', path: '/investments/orders', icon: ClipboardList },
  { label: 'Withdrawals & Loans', path: '/loans', icon: Banknote },
  { label: 'Beneficiaries', path: '/beneficiaries', icon: Users },
  { label: 'Retirement Tools', path: '/retirement-tools', icon: Calculator },
  { label: 'News & Insights', path: '/news', icon: Newspaper, badge: 'news' },
  { label: 'Compliance', path: '/compliance', icon: ShieldCheck, adminOnly: true },
  { label: 'OGE Dashboard', path: '/compliance/oge', icon: ShieldCheck, adminOnly: true },
  { label: 'Blind Trust', path: '/trust/portal', icon: Lock },
  { label: 'Documents', path: '/documents', icon: FolderOpen, badge: 'pendingActions' },
  { label: 'Account Profile', path: '/profile', icon: User },
];

function SidebarContent({ onNavigate }) {
  const location = useLocation();
  const { pendingActionsCount, iftRemaining } = useParticipant();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isAdmin = ADMIN_ROLES.has(role);

  const visibleNavItems = NAV_ITEMS.filter((i) => !i.adminOnly || isAdmin);

  // Count unread news items from localStorage (set by NewsPage badge clicks)
  const unreadNews = (() => {
    try { return parseInt(localStorage.getItem('news_unread_count') || '0', 10) || 0; }
    catch { return 0; }
  })();

  const badgeFor = (key) => {
    if (key === 'pendingActions') return pendingActionsCount > 0 ? pendingActionsCount : null;
    if (key === 'news') return unreadNews > 0 ? unreadNews : null;
    if (key === 'iftRemaining') return iftRemaining < 2 ? `${iftRemaining}` : null;
    return null;
  };

  return (
    <div className="flex flex-col h-full bg-[#1B2A4A]" data-testid="sidebar-nav">
      {/* Logo — links to dashboard */}
      <Link to="/dashboard" onClick={onNavigate} className="block px-3 py-4 border-b border-[#2D3F5F] hover:bg-white/5 transition-colors" data-testid="sidebar-logo-link">
        <img
          src="/unifed-logo-transparent.png"
          alt="UniFed One"
          className="h-14 w-auto brightness-0 invert mx-auto"
          data-testid="sidebar-logo"
        />
      </Link>

      <ScrollArea className="flex-1 py-3">
        <nav className="px-2 space-y-0.5">
          {visibleNavItems.map(({ label, path, icon: Icon, badge }) => {
            const isActive = location.pathname === path;
            const count = badge ? badgeFor(badge) : null;
            return (
              <NavLink
                key={path}
                to={path}
                onClick={onNavigate}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors duration-100 ${
                  isActive
                    ? 'bg-white/15 text-white font-medium'
                    : 'text-[#B8C5D8] hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon size={16} strokeWidth={1.5} />
                <span className="flex-1">{label}</span>
                {count != null && (
                  <span
                    className="min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[9px] font-bold"
                    data-testid={`nav-badge-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      <div className="px-2 py-3 border-t border-[#2D3F5F]">
        <NavLink
          to="/settings"
          onClick={onNavigate}
          data-testid="nav-settings"
          className="flex items-center gap-3 px-3 py-2.5 text-sm text-[#8B95A5] hover:bg-white/10 hover:text-white rounded transition-colors"
        >
          <Settings size={16} strokeWidth={1.5} />
          <span>Settings</span>
        </NavLink>
      </div>
    </div>
  );
}

function TopHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [exporting, setExporting] = useState(false);

  const currentPage = NAV_ITEMS.find(item => location.pathname === item.path);
  const pageTitle = currentPage?.label || 'UniFed One';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleExportSnapshot = async () => {
    setExporting(true);
    try {
      const { statementsAPI } = await import('@/lib/api');
      const res = await statementsAPI.exportSnapshot();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `UniFed_Snapshot_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Account snapshot downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Snapshot failed');
    } finally {
      setExporting(false);
    }
  };

  const showExport = !['admin', 'compliance', 'operations', 'restricted_admin']
    .includes((user?.role || '').toLowerCase());

  return (
    <header className="h-14 bg-white border-b border-[#E2E5E9] flex items-center justify-between px-4 lg:px-6 shrink-0" data-testid="top-header">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{pageTitle}</h2>
      </div>
      <div className="flex items-center gap-4">
        <Link to="/dashboard" data-testid="header-logo-link">
          <img
            src="/unifed-logo-transparent.png"
            alt="UniFed One"
            className="h-11 w-auto hidden md:block hover:opacity-80 transition-opacity"
            data-testid="header-logo"
          />
        </Link>
        <Separator orientation="vertical" className="h-6 bg-[#E2E5E9] hidden md:block" />
        {showExport && (
          <button
            onClick={handleExportSnapshot}
            disabled={exporting}
            data-testid="export-snapshot-btn"
            title="Download a real-time PDF snapshot of your account"
            className="flex items-center gap-1.5 text-xs text-[#1B2A4A] border border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-gray-50 rounded-full px-3 h-8 transition-colors disabled:opacity-50"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" strokeWidth={1.5} /> : <Download size={13} strokeWidth={1.5} />}
            <span className="hidden sm:inline">Export Account</span>
          </button>
        )}
        <span className="text-xs text-[#6B7280] font-mono hidden sm:inline" data-testid="user-email">{user?.email}</span>
        <Separator orientation="vertical" className="h-4 bg-[#E2E5E9]" />
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#DC2626] transition-colors"
        >
          <LogOut size={14} strokeWidth={1.5} />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
}

export function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8F9FA]" data-testid="app-shell">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-56 shrink-0">
        <SidebarContent onNavigate={() => {}} />
      </aside>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-56 bg-[#1B2A4A] border-r-0">
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center lg:block">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-3 text-[#6B7280] hover:text-[#1B2A4A]"
            data-testid="mobile-menu-button"
          >
            <Menu size={20} />
          </button>
          <div className="flex-1">
            <TopHeader />
          </div>
        </div>

        {/* Market Ticker */}
        <MarketTicker />

        <main className="flex-1 overflow-y-auto">
          <div className="p-4 lg:p-6 max-w-[1400px]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
